import {
  $create,
  $getState,
  $setState,
  createState,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementNode,
  LexicalNode,
} from 'lexical';
import { Source } from '../types';
import { isValidSource } from '../config/transformers/suggestion';

const COLORS = {
  default: {
    focused: 'var(--mantine-color-brand-0)',
    unfocused: 'var(--mantine-color-brand-1)',
  },
  note: {
    focused: 'var(--mantine-color-blueGray-0)',
    unfocused: 'var(--mantine-color-blueGray-2)',
  },
} as const;

type SuggestionState = {
  source: Source;
  focusedAt: number | null;
  isAbsolute: boolean;
  isBlock: boolean;
};

const suggestionState = createState<'suggestion-state', SuggestionState>('suggestion-state', {
  parse: () => {
    return {
      source: 'default',
      focusedAt: null,
      isAbsolute: false,
      isBlock: false,
    };
  },
});

export class SuggestionNode extends ElementNode {
  $config() {
    return this.config('suggestion', {
      extends: ElementNode,
      stateConfigs: [{ flat: true, stateConfig: suggestionState }],
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      suggestion: () => ({
        conversion: (element): DOMConversionOutput => {
          const sourceAttr = element.getAttribute('source');
          const source = isValidSource(sourceAttr) ? sourceAttr : 'default';
          const node = $createSuggestionNode(source);
          return { node };
        },
        priority: 0,
      }),
    };
  }

  createDOM(): HTMLElement {
    const currentState = $getState(this, suggestionState);
    const container = document.createElement(currentState.isBlock ? 'div' : 'span');
    container.dataset['suggestion'] = 'true';

    applySuggestionStyle(container, currentState);

    return container;
  }

  updateDOM(_prevNode: this, dom: HTMLElement): boolean {
    const currentState = $getState(this, suggestionState);
    applySuggestionStyle(dom, currentState);
    return false;
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement('suggestion');
    return { element: container };
  }
}

function applySuggestionStyle(element: HTMLElement, state: SuggestionState) {
  if (state.isBlock) {
    element.style.padding = '8px 8px 36px 8px';
    element.style.display = 'inline-block';
    element.style.width = '100%';
    element.style.borderRadius = '8px';
    element.style.position = 'relative';
  } else {
    element.style.padding = '2px 8px';
    element.style.display = 'inline-flex';
    element.style.alignItems = 'center';
    element.style.gap = '4px';
    element.style.verticalAlign = 'baseline';

    // Only inline suggestions can be absolute
    if (state.isAbsolute) {
      element.style.position = 'absolute';
      element.style.inset = '0';
      element.style.borderRadius = '0';
    } else {
      element.style.position = '';
      element.style.borderRadius = '4px';
    }
  }

  const bgColor = COLORS[state.source][state.focusedAt != null ? 'focused' : 'unfocused'];
  element.style.transition = 'background-color 0.4s ease-in-out';
  element.style.backgroundColor = bgColor;
}

export function $createSuggestionNode(
  source: Source = 'default',
  isBlock: boolean = false,
): SuggestionNode {
  return $setState($create(SuggestionNode), suggestionState, {
    source,
    focusedAt: null,
    isAbsolute: false,
    isBlock,
  });
}

export function $isSuggestionNode(node: LexicalNode | null | undefined): node is SuggestionNode {
  return node instanceof SuggestionNode;
}

export function $containingSuggestionNode(
  node: LexicalNode | null | undefined,
): SuggestionNode | null {
  while (node) {
    if ($isSuggestionNode(node)) {
      return node;
    }
    node = node.getParent();
  }
  return null;
}

export function $focusSuggestion(node: SuggestionNode) {
  const currentState = $getState(node, suggestionState);
  $setState(node, suggestionState, { ...currentState, focusedAt: Date.now() });
}

export function $sourceSuggestion(node: SuggestionNode) {
  const value = $getState(node, suggestionState);
  return value.source;
}

export function $getSuggestionState(node: SuggestionNode) {
  return $getState(node, suggestionState);
}

export function $setSuggestionAbsolute(node: SuggestionNode, isAbsolute: boolean) {
  const currentState = $getState(node, suggestionState);
  $setState(node, suggestionState, { ...currentState, isAbsolute });
  node.markDirty();
}
